import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentsIcon from '@mui/icons-material/Payments';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import { numFont } from '../../utils/fonts';
import { formatDateOnly } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';
import DataTable from '../shared/DataTable';
import { PL_API, purchaseErrorMessage, toNumber } from './purchaseUtils';

const BATCH_STATUS_COLOR = { open: 'info', file_generated: 'primary', confirmed: 'success', cancelled: 'default' };

export function BatchStatusChip({ status }) {
  const { t } = useTranslation();
  return <Chip size="small" color={BATCH_STATUS_COLOR[status] || 'default'} label={t(`purchaseLedger.batches.status.${status}`, { defaultValue: status })} />;
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Maksuun": pick approved invoices into a pain.001 batch, and list the batches. */
function PaymentBatchesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrganization } = useUserStore();
  const canPay = can(currentOrganization, 'purchase_invoices.pay');
  const [payable, setPayable] = useState([]);
  const [loadingPayable, setLoadingPayable] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [executionDate, setExecutionDate] = useState(todayIso());
  const [creating, setCreating] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadPayable = useCallback(async () => {
    setLoadingPayable(true);
    try {
      const response = await axios.get(`${PL_API}/payment-batches/payable/`, { params: showAll ? {} : { days: 7 } });
      setPayable(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (error) {
      setSnackbar({ open: true, message: purchaseErrorMessage(t, error), severity: 'error' });
    } finally {
      setLoadingPayable(false);
    }
  }, [showAll, t]);

  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const response = await axios.get(`${PL_API}/payment-batches/`, { params: { page_size: 50 } });
      setBatches(response.data?.results || []);
    } catch (error) {
      // list stays empty
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  useEffect(() => {
    loadPayable();
  }, [loadPayable]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // Preselect ?ids= from the list's "Lisää maksuerään" action, once the rows are known.
  const preselectApplied = useRef(false);
  useEffect(() => {
    if (preselectApplied.current) return;
    const ids = (searchParams.get('ids') || '').split(',').map((v) => parseInt(v, 10)).filter(Boolean);
    if (ids.length && payable.length) {
      const known = new Set(payable.map((p) => p.id));
      setSelected(new Set(ids.filter((id) => known.has(id))));
      if (!showAll && ids.some((id) => !known.has(id))) {
        setShowAll(true); // the wider list reloads; apply the selection once more against it
        return;
      }
      preselectApplied.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payable]);

  const groups = useMemo(() => {
    const map = new Map();
    payable.forEach((row) => {
      const key = row.due_date || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return [...map.entries()].sort(([a], [b]) => (a || '9999').localeCompare(b || '9999'));
  }, [payable]);

  const selectedRows = payable.filter((r) => selected.has(r.id));
  const total = selectedRows.reduce((sum, r) => sum + toNumber(r.open_amount ?? r.total_gross), 0);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (rows) => {
    const allSelected = rows.every((r) => selected.has(r.id));
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => (allSelected ? next.delete(r.id) : next.add(r.id)));
      return next;
    });
  };

  const createBatch = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${PL_API}/payment-batches/`, {
        invoice_ids: [...selected],
        requested_execution_date: executionDate || null,
      });
      navigate(`/ostolaskut/maksuerat/${response.data.id}`);
    } catch (error) {
      setSnackbar({ open: true, message: purchaseErrorMessage(t, error, 'purchaseLedger.batches.createFailed'), severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const batchColumns = [
    { key: 'number', label: t('purchaseLedger.batches.number'), width: '1%', render: (b) => <Typography variant="body2" sx={{ ...numFont, fontWeight: 600 }}>{b.number}</Typography> },
    { key: 'status', label: t('purchaseLedger.fields.status'), width: '1%', render: (b) => <BatchStatusChip status={b.status} /> },
    { key: 'requested_execution_date', label: t('purchaseLedger.batches.executionDate'), width: '1%', render: (b) => <Typography variant="body2" sx={numFont}>{formatDateOnly(b.requested_execution_date)}</Typography> },
    { key: 'item_count', label: t('purchaseLedger.batches.items'), width: '1%', align: 'right', render: (b) => <Typography variant="body2" sx={numFont}>{b.item_count}</Typography> },
    { key: 'total_amount', label: t('purchaseLedger.fields.totalGross'), width: '1%', align: 'right', render: (b) => <Typography variant="body2" sx={{ ...numFont, fontWeight: 600 }}>{formatPriceWithCurrency(toNumber(b.total_amount))}</Typography> },
    { key: 'created_by_name', label: t('purchaseLedger.batches.createdBy'), maxWidth: 200, render: (b) => <Typography variant="body2" noWrap>{b.created_by_name}</Typography> },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="payment-batches-page">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ostolaskut')} sx={{ mb: 1 }}>
        {t('purchaseLedger.backToList')}
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <PaymentsIcon color="secondary" />
        <Typography variant="h4" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
          {t('purchaseLedger.batches.title')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('purchaseLedger.batches.subtitle')}
      </Typography>

      {canPay && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }} data-testid="pl-batch-picker">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('purchaseLedger.batches.pickerTitle')}
            </Typography>
            <FormControlLabel control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />} label={t('purchaseLedger.batches.showAll')} />
          </Box>
          {loadingPayable ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : groups.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {showAll ? t('purchaseLedger.batches.nothingPayable') : t('purchaseLedger.batches.nothingDueSoon')}
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('purchaseLedger.fields.vendor')}</TableCell>
                    <TableCell>{t('purchaseLedger.fields.invoiceNumber')}</TableCell>
                    <TableCell align="right">{t('purchaseLedger.fields.openAmount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map(([due, rows]) => (
                    <React.Fragment key={due || 'none'}>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={rows.every((r) => selected.has(r.id))} indeterminate={rows.some((r) => selected.has(r.id)) && !rows.every((r) => selected.has(r.id))} onChange={() => toggleGroup(rows)} />
                        </TableCell>
                        <TableCell colSpan={3} sx={{ fontWeight: 600, ...numFont }}>
                          {due ? `${t('purchaseLedger.fields.dueDate')} ${formatDateOnly(due)}` : t('purchaseLedger.batches.noDueDate')}
                          {due && due < todayIso() && (
                            <Chip size="small" color="error" label={t('purchaseLedger.summary.overdue')} sx={{ ml: 1, height: 18, fontSize: 11 }} />
                          )}
                        </TableCell>
                      </TableRow>
                      {rows.map((row) => (
                        <TableRow key={row.id} hover onClick={() => toggle(row.id)} sx={{ cursor: 'pointer' }} data-testid={`pl-payable-${row.id}`}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={selected.has(row.id)} inputProps={{ 'data-testid': `pl-payable-check-${row.id}` }} />
                          </TableCell>
                          <TableCell>{row.vendor_name}</TableCell>
                          <TableCell sx={numFont}>{row.invoice_number}</TableCell>
                          <TableCell align="right" sx={{ ...numFont, fontWeight: 600 }}>
                            {formatPriceWithCurrency(toNumber(row.open_amount ?? row.total_gross))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <TextField
              size="small"
              type="date"
              label={t('purchaseLedger.batches.executionDate')}
              value={executionDate}
              onChange={(e) => setExecutionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'pl-batch-execution-date' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={numFont}>
                {t('purchaseLedger.batches.selectedTotal', { count: selected.size })} <strong>{formatPriceWithCurrency(total)}</strong>
              </Typography>
              <Button variant="contained" color="secondary" disabled={creating || selected.size === 0} onClick={createBatch} startIcon={creating ? <CircularProgress size={16} /> : <PaymentsIcon />} data-testid="pl-create-batch">
                {t('purchaseLedger.batches.create')}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {t('purchaseLedger.batches.listTitle')}
      </Typography>
      <DataTable
        columns={batchColumns}
        data={batches}
        loading={loadingBatches}
        onRowClick={(b) => navigate(`/ostolaskut/maksuerat/${b.id}`)}
        emptyIcon={<PaymentsIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
        emptyTitle={t('purchaseLedger.batches.empty')}
        emptyDescription={t('purchaseLedger.batches.emptyHint')}
        testIdPrefix="pl-batch-row"
      />
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentBatchesPage;
